// This file is now a wrapper around API calls to the backend
// It maintains backward compatibility with the existing codebase

import { fetchProducts, createProduct, updateProduct, deleteProduct } from '../services/api';

// Initialize products from API or fallback to localStorage for offline capability
const getInitialProducts = async () => {
  try {
    // Try to fetch from API first
    console.log('Attempting to fetch products from API...');
    const productsData = await fetchProducts();
    
    // Check if we received valid data (protection against empty responses)
    if (!productsData || 
        !productsData.smoothies || 
        !productsData.juices || 
        !productsData.proteinBalls ||
        productsData.smoothies.length === 0 && 
        productsData.juices.length === 0 && 
        productsData.proteinBalls.length === 0) {
      console.warn('API returned empty or invalid product data, using fallback data');
      throw new Error('Invalid or empty product data received from API');
    }
    
    // If successful, save to localStorage as a cache
    localStorage.setItem('reviveProducts', JSON.stringify(productsData));
    console.log('Successfully loaded products from API:', productsData);
    return productsData;
  } catch (error) {
    console.error('Failed to fetch products from API, using fallback:', error);
    
    // Fallback to localStorage if API call fails
    const savedProducts = localStorage.getItem('reviveProducts');
    
    // If we have saved products, use them
    if (savedProducts) {
      console.log('Using cached products from localStorage');
      const parsedProducts = JSON.parse(savedProducts);
      
      // Check if localStorage data is valid
      if (parsedProducts.smoothies.length > 0 || 
          parsedProducts.juices.length > 0 || 
          parsedProducts.proteinBalls.length > 0) {
        return parsedProducts;
      }
    }
    
    // If no saved products or invalid data, use default products
    console.log('Using default sample products');
    
    // Save default products to localStorage for future use
    localStorage.setItem('reviveProducts', JSON.stringify(defaultProducts));
    
    return defaultProducts;
  }
};

// Default product data to use when no data is available
const defaultProducts = {
  smoothies: [
    {
      id: 's1',
      name: 'Green Goddess',
      description: 'Spinach, kale, banana, pineapple, and coconut water.',
      price: 8.99,
      category: 'smoothies',
      imageUrl: 'https://images.unsplash.com/photo-1502741224143-90386d7f8c82?q=80&w=500&auto=format&fit=crop',
      ingredients: ['Spinach', 'Kale', 'Banana', 'Pineapple', 'Coconut Water'],
      nutritionFacts: {
        calories: 220,
        protein: 5,
        fat: 1,
        carbs: 48
      }
    },
    {
      id: 's2',
      name: 'Berry Blast',
      description: 'Strawberries, blueberries, raspberries, Greek yogurt, and almond milk.',
      price: 9.49,
      category: 'smoothies',
      imageUrl: 'https://images.unsplash.com/photo-1563227812-0ea4c22e6cc8?q=80&w=500&auto=format&fit=crop',
      ingredients: ['Strawberries', 'Blueberries', 'Raspberries', 'Greek Yogurt', 'Almond Milk'],
      nutritionFacts: {
        calories: 245,
        protein: 10,
        fat: 3,
        carbs: 42
      }
    }
  ],
  juices: [
    {
      id: 'j1',
      name: 'Immunity Boost',
      description: 'Orange, carrot, ginger, turmeric, and lemon.',
      price: 7.99,
      category: 'juices',
      imageUrl: 'https://images.unsplash.com/photo-1622597466356-096bf5971e84?q=80&w=500&auto=format&fit=crop',
      ingredients: ['Orange', 'Carrot', 'Ginger', 'Turmeric', 'Lemon'],
      nutritionFacts: {
        calories: 120,
        protein: 2,
        fat: 0,
        carbs: 28
      }
    }
  ],
  proteinBalls: [
    {
      id: 'p1',
      name: 'Peanut Butter Chocolate',
      description: 'Oats, peanut butter, chocolate chips, honey, and chia seeds.',
      price: 2.99,
      category: 'proteinBalls',
      imageUrl: 'https://images.unsplash.com/photo-1612886623532-5db6195bd332?q=80&w=500&auto=format&fit=crop',
      ingredients: ['Oats', 'Peanut Butter', 'Chocolate Chips', 'Honey', 'Chia Seeds'],
      nutritionFacts: {
        calories: 120,
        protein: 6,
        fat: 7,
        carbs: 12
      }
    }
  ]
};

// Get products synchronously from localStorage for immediate display
// This will be updated by the API call when it completes
const getLocalProducts = () => {
  const savedProducts = localStorage.getItem('reviveProducts');
  return savedProducts ? JSON.parse(savedProducts) : defaultProducts;
};

// Default export is synchronous for backward compatibility
const products = getLocalProducts();

// Save products to API and update localStorage
export const saveProducts = async (updatedProducts) => {
  // Update localStorage immediately for UI responsiveness
  localStorage.setItem('reviveProducts', JSON.stringify(updatedProducts));
  
  // We don't await this - it's a background operation
  updateBackendProducts(updatedProducts);
};

// This function handles the actual API updates
const updateBackendProducts = async (updatedProducts) => {
  try {
    // We'd need to compare old and new products to determine what changed
    // This is a simplified approach
    const currentProducts = getLocalProducts();
    
    // Process each category
    for (const category of Object.keys(updatedProducts)) {
      const currentInCategory = new Map(
        currentProducts[category]?.map(p => [p.id, p]) || []
      );
      
      // Check each product in the updated list
      for (const product of updatedProducts[category]) {
        if (!currentInCategory.has(product.id)) {
          // New product - create it
          await createProduct(product);
        } else {
          // Compare with current version
          const current = currentInCategory.get(product.id);
          if (JSON.stringify(current) !== JSON.stringify(product)) {
            // Updated product
            await updateProduct(product.id, product);
          }
        }
        // Remove from map to track deletions
        currentInCategory.delete(product.id);
      }
      
      // Remaining items in the map were deleted
      for (const [deletedId] of currentInCategory.entries()) {
        await deleteProduct(deletedId);
      }
    }
  } catch (error) {
    console.error('Error updating products in backend:', error);
    // Could add retry logic here if needed
  }
};

// Generate a unique ID for new products
export const generateId = (category) => {
  const prefix = category.charAt(0);
  const existingIds = products[category]?.map(product => product.id) || [];
  let counter = 1;
  let newId = `${prefix}${counter}`;
  
  while (existingIds.includes(newId)) {
    counter++;
    newId = `${prefix}${counter}`;
  }
  
  return newId;
};

// Initialize products from API
export const initializeProducts = async () => {
  const apiProducts = await getInitialProducts();
  return apiProducts;
};

export default products;